import ArqNavigation from "@/components/arqera/ArqNavigation";
import ArqFooter from "@/components/arqera/ArqFooter";
import { SEO } from "@/components/SEO";
import { Check, Quote } from "lucide-react";

const findings = [
  "16 to 19 tracking cookies firing before any user interaction across both GB and US-CA regions",
  "Zero reduction in active trackers after consent rejection, cookie count actually increased post opt-out",
  "Google Consent Mode signals set to implicit, meaning Google treated all users as having consented regardless of their selection",
  "9 High-risk third-party trackers confirmed active after rejection including Meta Pixel, LinkedIn Insight, and Twitter/X advertising scripts",
  "Global Privacy Control (GPC) not honored in either region",
];

const actions = [
  "Configured Google Consent Mode v2 to deny-by-default on every page load before any tags fire",
  "Fixed 5 previously ungated GTM tags with trigger groups mapped to correct consent categories",
  "Deployed OptanonWrapper cookie deletion script via WordPress, confirmed all 5 target cookies deleted on opt-out in production",
  "Updated OneTrust cookie inventory and configured GPC for California users under CPRA",
  "Conducted pre and post-remediation validation testing with pass/fail confirmation per vendor and per cookie",
];

const results = [
  "5 of 5 Privaini findings remediated and confirmed in production",
  "Zero post-rejection tracking calls confirmed across Meta, LinkedIn, and Twitter/X",
  "Insurance-ready evidence package delivered within the engagement",
];

const shortForm = [
  {
    industry: "Healthcare SaaS",
    title: "AI Scribe Pre-Launch Risk Closure",
    trigger: "Enterprise client procurement security review",
    challenge:
      "A healthcare SaaS shipping an AI clinical scribe needed to close 14 findings from an enterprise prospect's pre-procurement security questionnaire before contract signature, including PHI handling, model isolation, and BAA-aligned subprocessor disclosure.",
    actions: [
      "Threat-modeled the scribe against MITRE ATLAS and HIPAA Security Rule",
      "Hardened authorization between tenant data and inference layer",
      "Authored BAA-aligned subprocessor list and AI governance policy",
      "Produced an evidence package mapped 1:1 to the prospect's questionnaire",
    ],
    outcome: [
      "14 of 14 findings closed in 5 weeks",
      "Procurement signed without security exception",
      "Reusable HIPAA + AI evidence pack now used in subsequent enterprise deals",
    ],
  },
  {
    industry: "Financial Services",
    title: "SOC 2 Type II Readiness for an AI-Embedded Workflow Tool",
    trigger: "Annual SOC 2 cycle with new AI feature in scope",
    challenge:
      "A financial services workflow vendor introduced an LLM-powered feature mid-cycle and needed it scoped into their SOC 2 Type II without delaying the audit window. Existing controls did not cover model access, prompt logging retention, or third-party inference subprocessor risk.",
    actions: [
      "Mapped the AI feature against existing CC and A series controls",
      "Implemented prompt and response logging with retention aligned to audit period",
      "Documented inference subprocessor flow and added vendor to TPRM register",
      "Drafted control narratives and walkthrough evidence for the auditor",
    ],
    outcome: [
      "AI feature included in scope without audit window slippage",
      "Zero exceptions on AI-specific controls in the final report",
      "Control language reused for the next year's renewal",
    ],
  },
];

export default function CaseStudies() {
  return (
    <div className="min-h-screen bg-arq-bg text-arq-text">
      <SEO
        title="Case Studies, ArqeraAI | Security & Privacy Remediation"
        description="Anonymized engagement case studies from ArqeraAI: how we close insurance-flagged consent and security findings with production-validated evidence."
        canonical="https://arqeraai.com/case-studies"
      />
      <ArqNavigation />

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-[920px] mx-auto">
          <div className="arq-label mb-5">Case Studies</div>
          <h1
            className="font-display font-semibold text-arq-text mb-6"
            style={{ fontSize: "clamp(32px, 5vw, 48px)", lineHeight: 1.15 }}
          >
            Anonymized engagements. Real findings. Production-validated outcomes.
          </h1>
          <p className="text-arq-muted text-[17px] leading-[1.75] mb-12">
            Names withheld under NDA. Numbers, findings, and outcomes are taken directly from the
            engagement record. Additional case studies available under NDA on request.
          </p>

          {/* Featured */}
          <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary mb-4">
            Featured Engagement
          </div>
          <h2
            className="font-display font-semibold text-arq-text mb-6"
            style={{ fontSize: "clamp(24px, 3.4vw, 34px)", lineHeight: 1.2 }}
          >
            Insurance-Flagged Consent Violations Remediated in Production
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 mb-12">
            {[
              { label: "Client", value: "Mid-market logistics company (identity withheld)" },
              { label: "Industry", value: "Logistics / Supply Chain" },
              { label: "Trigger", value: "Cyber insurance carrier independent privacy scan" },
            ].map((m) => (
              <div key={m.label} className="arq-card p-5">
                <div className="font-mono-tech text-[10px] tracking-[0.12em] uppercase text-arq-tertiary mb-1.5">
                  {m.label}
                </div>
                <div className="text-arq-text text-[14px] leading-[1.5]">{m.value}</div>
              </div>
            ))}
          </div>

          <Section heading="The Challenge">
            <p>
              A mid-market logistics company received a formal finding from their cyber insurance
              carrier following an independent Privaini privacy scan of their public website. The
              scan identified that non-essential advertising and social media tracking technologies
             , including Meta, LinkedIn, and Twitter/X, remained active after users selected
              Reject All on the consent banner. The finding created a documented compliance gap
              under CCPA/CPRA and put the client's insurance renewal at risk.
            </p>
            <p>
              The organization had OneTrust deployed and believed their consent implementation was
              compliant. The scan proved otherwise, the consent UI was cosmetically functional but
              enforcement was completely broken.
            </p>
          </Section>

          <Section heading="What We Found">
            <BulletList items={findings} />
          </Section>

          <Section heading="What We Did">
            <BulletList items={actions} />
          </Section>

          <Section heading="The Outcome">
            <p>
              Following remediation, independent validation confirmed that Meta, LinkedIn, and
              Twitter/X tracking technologies were fully suppressed after consent rejection.
              Post-rejection beacon monitoring showed zero outbound calls for 151 seconds across
              all previously flagging vendors. Cookie deletion was confirmed in production for all
              five target cookies.
            </p>
            <p>
              The client requested a formal Privaini rescan to close the insurance finding with an
              independent evidence artifact. They entered the carrier conversation with a
              documented remediation record and production-validated confirmation of compliance.
            </p>
          </Section>

          <div className="arq-card p-8 mb-12">
            <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary mb-4">
              Results at a Glance
            </div>
            <ul className="space-y-3">
              {results.map((r) => (
                <li key={r} className="flex items-start gap-3 text-[15px] text-arq-text">
                  <Check className="w-4 h-4 mt-1 flex-shrink-0 text-arq-teal" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <figure className="border-l-2 border-arq-blue pl-6 py-4 mb-20">
            <Quote className="w-6 h-6 text-arq-blue mb-3 opacity-70" />
            <blockquote className="font-display text-[20px] md:text-[22px] text-arq-text leading-[1.5] italic mb-3">
              "We thought having OneTrust meant we were covered. ArqeraAI showed us the gap, closed
              it, and gave us the evidence file our carrier was looking for."
            </blockquote>
            <figcaption className="text-arq-muted text-[14px]">
             , Chief Information Security Officer, Logistics Company [name withheld]
            </figcaption>
          </figure>

          {/* Short-form additional case studies */}
          <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-tertiary mb-4">
            Additional Engagements
          </div>
          <h2
            className="font-display font-semibold text-arq-text mb-8"
            style={{ fontSize: "clamp(22px, 3vw, 28px)", lineHeight: 1.2 }}
          >
            Selected short-form summaries.
          </h2>

          <div className="space-y-6 mb-16">
            {shortForm.map((cs) => (
              <article key={cs.title} className="arq-card p-7 md:p-8">
                <div className="font-mono-tech text-[10px] tracking-[0.12em] uppercase text-arq-tertiary mb-2">
                  {cs.industry} · {cs.trigger}
                </div>
                <h3 className="font-display text-[20px] md:text-[22px] font-semibold text-arq-text mb-4">
                  {cs.title}
                </h3>
                <p className="text-arq-muted text-[15px] leading-[1.7] mb-5">{cs.challenge}</p>

                <div className="grid md:grid-cols-2 gap-6 pt-5 border-t border-arq-border">
                  <div>
                    <div className="font-mono-tech text-[10px] tracking-[0.1em] uppercase text-arq-tertiary mb-3">
                      What We Did
                    </div>
                    <ul className="space-y-2">
                      {cs.actions.map((a) => (
                        <li key={a} className="flex items-start gap-2.5 text-[14px] text-arq-text/90 leading-[1.55]">
                          <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-arq-teal" />
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-mono-tech text-[10px] tracking-[0.1em] uppercase text-arq-tertiary mb-3">
                      Outcome
                    </div>
                    <ul className="space-y-2">
                      {cs.outcome.map((o) => (
                        <li key={o} className="flex items-start gap-2.5 text-[14px] text-arq-text/90 leading-[1.55]">
                          <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-arq-teal" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* CTA */}
          <div className="arq-card p-8 text-center">
            <h3 className="font-display text-[22px] md:text-[24px] font-semibold text-arq-text mb-3">
              Have a finding you need to close?
            </h3>
            <p className="text-arq-muted text-[15px] mb-6 max-w-[520px] mx-auto">
              Bring us your insurance scan, audit finding, or compliance gap. We will scope a
              remediation engagement with a written proposal in 48 hours.
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center justify-center bg-arq-blue text-white font-semibold text-[15px] px-7 py-3.5 rounded-md hover:bg-arq-blue-bright transition-colors"
            >
              Schedule Assessment
            </a>
          </div>
        </div>
      </main>

      <ArqFooter />
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="mb-12">
      <h2 className="font-display text-[22px] md:text-[26px] font-semibold text-arq-text mb-4">
        {heading}
      </h2>
      <div className="space-y-4 text-arq-muted text-[16px] leading-[1.75]">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-[15px] text-arq-text/90">
          <Check className="w-4 h-4 mt-1.5 flex-shrink-0 text-arq-teal" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}
